/* Signature — shared utilities. No dependencies, progressive enhancement only. */
(function () {
  'use strict';

  /** Debounce a function. */
  window.sigDebounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  /** Announce a message to screen readers via the global polite region. */
  window.sigAnnounce = function (message) {
    const region = document.getElementById('sr-announcer');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  /**
   * Fetch a rendered section and return its parsed root element.
   * @param {string} sectionId - section to render
   * @param {string} [url] - base url (defaults to current path, keeps params)
   */
  window.sigFetchSection = async function (sectionId, url) {
    const base = new URL(url || window.location.href, window.location.origin);
    base.searchParams.set('section_id', sectionId);
    const res = await fetch(base.toString(), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.querySelector('.shopify-section') || doc.body;
  };

  function isDialog(element) {
    return Boolean(element && element.tagName === 'DIALOG');
  }

  /** Open a <dialog> with scroll lock + focus restore. */
  window.sigOpenDialog = function (dialog, opener) {
    if (!dialog) return false;
    if (dialog.open) return true;
    // Older embedded browsers keep the full-page shopping paths usable.
    if (typeof dialog.showModal !== 'function') {
      let destination = opener && opener.getAttribute && opener.getAttribute('href');
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      if (!destination || destination.charAt(0) === '#') {
        if (dialog.id === 'nav-drawer') destination = root + 'collections';
        else if (dialog.id === 'cart-drawer') destination = root + 'cart';
        else if (dialog.id === 'search-dialog') destination = root + 'search';
        else if (dialog.id === 'zoom-dialog') {
          const image = dialog.querySelector('img');
          destination = image && image.src;
        }
      }
      if (destination && destination.charAt(0) !== '#') window.location.assign(destination);
      return false;
    }
    dialog.__opener = opener || document.activeElement;
    dialog.setAttribute('scroll-lock', '');
    dialog.showModal();
    const target = dialog.querySelector('[autofocus]') || dialog.querySelector('button, [href], input, select, textarea');
    if (target) target.focus();
    return true;
  };

  /** Close a dialog and restore focus to its opener. */
  window.sigCloseDialog = function (dialog) {
    if (!dialog || !dialog.open || typeof dialog.close !== 'function') return;
    dialog.close();
  };

  document.addEventListener('close', function (e) {
    const dialog = e.target;
    if (!isDialog(dialog)) return;
    dialog.removeAttribute('scroll-lock');
    const opener = dialog.__opener;
    dialog.__opener = null;
    if (opener && opener.isConnected) opener.focus();
  }, true);

  /* Route the native Escape-key cancellation through the shared close path. */
  document.addEventListener('cancel', function (e) {
    const dialog = e.target;
    if (!isDialog(dialog) || !dialog.open) return;
    e.preventDefault();
    window.sigCloseDialog(dialog);
  }, true);

  /* A populated input[type="search"] consumes Escape before a dialog can
     emit cancel. Capture the key first so every modal keeps one-key close. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
    const focusedDialog = e.target && e.target.closest
      ? e.target.closest('dialog[open]')
      : null;
    const openDialogs = document.querySelectorAll('dialog[open]');
    const dialog = focusedDialog || openDialogs[openDialogs.length - 1];
    if (!isDialog(dialog) || !dialog.open) return;
    e.preventDefault();
    window.sigCloseDialog(dialog);
  }, true);

  /* Wire any [data-dialog-open="#id"] / [data-dialog-close] triggers. */
  document.addEventListener('click', function (e) {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.querySelector(opener.getAttribute('data-dialog-open'));
      if (dialog) {
        e.preventDefault();
        window.sigOpenDialog(dialog, opener);
      }
      return;
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) window.sigCloseDialog(dialog);
    }
  });

  function isBackdropPointer(e) {
    const dialog = e.target;
    if (!isDialog(dialog) || !dialog.open) return false;
    const rect = dialog.getBoundingClientRect();
    return (
      e.clientX < rect.left || e.clientX > rect.right ||
      e.clientY < rect.top || e.clientY > rect.bottom
    );
  }

  /* Require both ends of a mouse, pen, or touch gesture on the backdrop. */
  let backdropPress = null;
  document.addEventListener('pointerdown', function (e) {
    backdropPress = isBackdropPointer(e)
      ? { dialog: e.target, pointerId: e.pointerId }
      : null;
  }, true);
  document.addEventListener('pointerup', function (e) {
    const press = backdropPress;
    backdropPress = null;
    if (
      press &&
      press.pointerId === e.pointerId &&
      press.dialog === e.target &&
      isBackdropPointer(e)
    ) {
      window.sigCloseDialog(press.dialog);
    }
  }, true);
  document.addEventListener('pointercancel', function () {
    backdropPress = null;
  }, true);

  /* Quantity steppers: [data-qty] wrapping buttons + input. */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const delta = Number(btn.getAttribute('data-qty-change'));
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + delta);
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();
